import * as React from "react";
import { observer } from "mobx-react-lite";
import {
    Dropdown,
    IDropdownOption,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    Stack,
    Label,
    StackItem,
    IStackTokens,
} from "@fluentui/react";

import * as ReorderPlayersDialogController from "../../components/dialogs/ReorderPlayersDialogController";
import { Player } from "../../state/TeamState";
import { AppState } from "../../state/AppState";
import { useAppState } from "../../contexts/StateContext";
import { ReorderPlayersDialogState } from "../../state/ReorderPlayersDialogState";
import { PlayerRoster } from "../PlayerRoster";
import { ModalVisibilityStatus } from "../../state/ModalVisibilityStatus";
import { ModalDialog } from "./ModalDialog";

const dialogBodyTokens: IStackTokens = { childrenGap: 10 };

// TODO: Look into making a DefaultDialog, which handles the footers and default props
export const ReorderPlayerDialog = observer(function ReorderPlayerDialog(): JSX.Element {
    const appState: AppState = useAppState();

    return (
        <ModalDialog
            title="Reorder Players"
            visibilityStatus={ModalVisibilityStatus.ReorderPlayers}
            onDismiss={() => ReorderPlayersDialogController.hideDialog(appState)}
        >
            <ReorderPlayerDialogBody appState={appState} />
            <DialogFooter>
                <PrimaryButton text="OK" onClick={() => ReorderPlayersDialogController.submit(appState)} />
                <DefaultButton text="Cancel" onClick={() => ReorderPlayersDialogController.hideDialog(appState)} />
            </DialogFooter>
        </ModalDialog>
    );
});

const ReorderPlayerDialogBody = observer(function ReorderPlayerDialogBody(
    props: IReorderPlayerDialogBodyProps
): JSX.Element {
    const appState: AppState = props.appState;

    const teamChangeHandler = (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        if (option?.text != undefined) {
            ReorderPlayersDialogController.changeTeamName(appState, option.text);
        }
    };

    const moveBackwardHandler = (player: Player): void => ReorderPlayersDialogController.moveBackward(appState, player);
    const moveForwardHandler = (player: Player): void => ReorderPlayersDialogController.moveForward(appState, player);
    const moveToIndexHandler = (player: Player, index: number): void =>
        ReorderPlayersDialogController.movePlayerToIndex(appState, player, index);

    const reorderPlayersDialog: ReorderPlayersDialogState | undefined =
        appState.uiState.dialogState.reorderPlayersDialog;
    if (reorderPlayersDialog === undefined) {
        return <></>;
    }

    const teamOptions: IDropdownOption[] = appState.activeGame.teamNames.map((teamName, index) => {
        return {
            key: index,
            text: teamName,
            selected: reorderPlayersDialog.teamName === teamName,
        };
    });

    const players: Player[] = reorderPlayersDialog.players.filter((p) => p.teamName === reorderPlayersDialog.teamName);

    return (
        <Stack tokens={dialogBodyTokens}>
            <StackItem>
                <Label>
                    Drag and drop players into the desired position, or use the up and down buttons next to the player
                    to move them up or down the list.
                </Label>
            </StackItem>
            <StackItem>
                <Dropdown label="Team" options={teamOptions} onChange={teamChangeHandler} />
            </StackItem>
            <StackItem>
                <PlayerRoster
                    canSetStarter={false}
                    players={players}
                    onMovePlayerBackward={moveBackwardHandler}
                    onMovePlayerForward={moveForwardHandler}
                    onMovePlayerToIndex={moveToIndexHandler}
                />
            </StackItem>
        </Stack>
    );
});

interface IReorderPlayerDialogBodyProps {
    appState: AppState;
}
